import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';
import styled from 'styled-components';

// Premium color palette
const COLORS = {
  primary: '#1e293b',
  secondary: '#334155',
  accent: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  dark: '#0f172a',
  light: '#f8fafc',
  white: '#ffffff',
  sidebar: '#1e293b',
  sidebarHover: '#334155',
  text: '#e2e8f0',
  textMuted: '#94a3b8',
  border: '#334155',
  gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
};

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${COLORS.light};
  overflow-x: hidden;
`;

const Sidebar = styled.aside`
  width: ${props => props.$collapsed ? '80px' : '280px'};
  background: ${COLORS.sidebar};
  border-right: 1px solid ${COLORS.border};
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  left: 0;
  top: 0;
  z-index: 1000;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease;
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.5rem;
  border-bottom: 1px solid ${COLORS.border};
  background: linear-gradient(135deg, ${COLORS.dark} 0%, ${COLORS.primary} 100%);
  display: flex;
  align-items: center;
  justify-content: ${props => props.$collapsed ? 'center' : 'space-between'};
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: ${COLORS.text};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${COLORS.sidebarHover};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${COLORS.white};
`;

const LogoIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${COLORS.gradient};
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const LogoText = styled.div`
  display: ${props => props.$collapsed ? 'none' : 'block'};
  opacity: ${props => props.$collapsed ? 0 : 1};
  transition: opacity 0.3s ease;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const LogoSubtitle = styled.div`
  font-size: 0.75rem;
  opacity: 0.8;
  display: ${props => props.$collapsed ? 'none' : 'block'};
  opacity: ${props => props.$collapsed ? 0 : 0.8};
  transition: opacity 0.3s ease;
`;

const Navigation = styled.nav`
  flex: 1;
  padding: ${({ $collapsed }) => ($collapsed ? '1.5rem 0' : '1.5rem 0')};
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
`;

const NavSection = styled.div`
  margin-bottom: 2rem;
`;

const NavSectionTitle = styled.div`
  padding: 0 1.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${COLORS.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const NavItem = styled.div`
  margin-bottom: 0.25rem;
`;

const NavLink = styled.button`
  width: 100%;
  padding: ${({ $collapsed }) => ($collapsed ? '0.75rem' : '0.75rem 1.5rem')};
  background: none;
  border: none;
  color: ${COLORS.text};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${({ $collapsed }) => ($collapsed ? '0' : '0.75rem')};
  font-size: ${({ $collapsed }) => ($collapsed ? '1.5rem' : '1rem')};
  border-radius: 0.5rem;
  margin: ${({ $collapsed }) => ($collapsed ? '0 0.5rem' : '0 0.75rem')};
  position: relative;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  min-height: ${({ $collapsed }) => ($collapsed ? '48px' : 'auto')};
  
  &:hover {
    background: ${COLORS.sidebarHover};
    color: ${COLORS.white};
  }
  
  &.active {
    background: ${COLORS.accent};
    color: ${COLORS.white};
    
    &::before {
      content: '';
      position: absolute;
      left: ${({ $collapsed }) => ($collapsed ? 'auto' : '-0.75rem')};
      right: ${({ $collapsed }) => ($collapsed ? '0.5rem' : 'auto')};
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 24px;
      background: ${COLORS.accent};
      border-radius: ${({ $collapsed }) => ($collapsed ? '2px 0 0 2px' : '0 2px 2px 0')};
    }
  }
  
  span {
    display: ${({ $collapsed }) => ($collapsed ? 'none' : 'inline')};
    opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
    transition: opacity 0.3s ease;
  }
`;

// const NavIcon = styled.div`
//   width: 20px;
//   height: 20px;
//   display: flex;
//   align-items: center;
//   justify-content: center;
// `;

const SidebarFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid ${COLORS.border};
  margin-top: auto;
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: ${props => props.$collapsed ? '0.875rem' : '0.875rem 1.5rem'};
  background: transparent;
  color: ${COLORS.text};
  border: 1px solid ${COLORS.border};
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.$collapsed ? '0' : '0.75rem'};
  margin: ${props => props.$collapsed ? '0 0.5rem' : '0 0.75rem'};
  min-height: ${props => props.$collapsed ? '48px' : 'auto'};
  
  &:hover {
    background: ${COLORS.danger};
    color: ${COLORS.white};
    border-color: ${COLORS.danger};
  }
  
  span {
    display: ${props => props.$collapsed ? 'none' : 'inline'};
    opacity: ${props => props.$collapsed ? 0 : 1};
    transition: opacity 0.3s ease;
  }
  
  .nav-icon {
    flex-shrink: 0;
    font-size: ${props => props.$collapsed ? '1.5rem' : '1.25rem'};
    width: 24px;
    height: 24px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${props => props.$collapsed ? '80px' : '280px'};
  padding: 2rem;
  background: ${COLORS.light};
  min-height: 100vh;
  transition: margin-left 0.3s ease;
`;

const ContentWrapper = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.header`
  background: ${COLORS.white};
  border-radius: 1rem;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${COLORS.primary};
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  color: ${COLORS.textMuted};
  margin: 0.25rem 0 0 0;
  font-size: 0.875rem;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatusBadge = styled.div`
  padding: 0.5rem 1rem;
  background: ${COLORS.success};
  color: ${COLORS.white};
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  background: ${COLORS.white};
  border-radius: 50%;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const AdminLayout = ({ children }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    // Check authentication state
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        // Redirect to admin login if not authenticated
        navigate('/admin');
      }
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    // Set active tab based on current URL
    const path = location.pathname;
    if (path.includes('/admin/dashboard')) {
      setActiveTab('dashboard');
    } else if (path.includes('/admin/bookings')) {
      setActiveTab('bookings');
    } else if (path.includes('/admin/rooms')) {
      setActiveTab('rooms');
    } else if (path.includes('/admin/gallery')) {
      setActiveTab('gallery');
    } else if (path.includes('/admin/facilities')) {
      setActiveTab('facilities');
    } else if (path.includes('/admin/hero')) {
      setActiveTab('hero');
    } else if (path.includes('/admin/messages')) {
      setActiveTab('messages');
    } else if (path.includes('/admin/video-testimonials')) {
      setActiveTab('video-testimonials');
    }
  }, [location]);

  const handleNavigation = (tab) => {
    setActiveTab(tab);
    navigate(`/admin/${tab}`);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Show loading state while checking authentication
  if (user === null) {
    return (
      <LayoutContainer>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '1.2rem',
          color: COLORS.text
        }}>
          Checking authentication...
        </div>
      </LayoutContainer>
    );
  }

  // Redirect if not authenticated
  if (user === false) {
    return null; // Will be redirected by onAuthStateChanged
  }

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: '📊',
      path: '/admin/dashboard'
    },
    {
      id: 'bookings',
      label: 'Bookings',
      icon: '📅',
      path: '/admin/bookings'
    },
    {
      id: 'rooms',
      label: 'Rooms',
      icon: '🏨',
      path: '/admin/rooms'
    },
    {
      id: 'gallery',
      label: 'Gallery',
      icon: '🖼️',
      path: '/admin/gallery'
    },
    {
      id: 'video-testimonials',
      label: 'Video Testimonials',
      icon: '🎥',
      path: '/admin/video-testimonials'
    },
    {
      id: 'facilities',
      label: 'Facilities',
      icon: '🏊',
      path: '/admin/facilities'
    },
    {
      id: 'hero',
      label: 'Hero Management',
      icon: '🎨',
      path: '/admin/hero'
    },
    {
      id: 'messages',
      label: 'Messages',
      icon: '💬',
      path: '/admin/messages'
    }
  ];

  return (
    <LayoutContainer>
      <Sidebar $collapsed={isCollapsed}>
        <SidebarHeader $collapsed={isCollapsed}>
          <Logo>
            <LogoIcon>W</LogoIcon>
            <div>
            <LogoText $collapsed={isCollapsed}>Wular Admin</LogoText>
            <LogoSubtitle $collapsed={isCollapsed}>Management Panel</LogoSubtitle>
         </div>
          </Logo>
          
          <ToggleButton onClick={toggleSidebar}>
            {isCollapsed ? '→' : '←'}
          </ToggleButton>
        </SidebarHeader>

        <Navigation $collapsed={isCollapsed}>
          <NavSection>
            <NavSectionTitle $collapsed={isCollapsed}>Main</NavSectionTitle>
            {navigationItems.map((item) => (
              <NavItem key={item.id}>
                <NavLink
                  href={item.path}
                  className={activeTab === item.id ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigation(item.id);
                  }}
                  $collapsed={isCollapsed}
                  title={isCollapsed ? item.label : ''}
                >
                  {item.icon}
                 {!isCollapsed && <span>{item.label}</span>}
                </NavLink>
              </NavItem>
            ))}
          </NavSection>
        </Navigation>

        <SidebarFooter $collapsed={isCollapsed}>
          <LogoutButton onClick={handleLogout} $collapsed={isCollapsed}>
            🚪
            <span>Logout</span>
          </LogoutButton>
        </SidebarFooter>
      </Sidebar>

      <MainContent $collapsed={isCollapsed}>
        <ContentWrapper>
          <Header>
            <div>
              <HeaderTitle>
                {navigationItems.find(item => item.id === activeTab)?.label || 'Admin Panel'}
              </HeaderTitle>
              <HeaderSubtitle>
                Manage your resort operations efficiently
              </HeaderSubtitle>
            </div>
            <HeaderActions>
              <StatusBadge>
                <StatusDot />
                System Online
              </StatusBadge>
            </HeaderActions>
          </Header>
          
          {children}
        </ContentWrapper>
      </MainContent>
    </LayoutContainer>
  );
};

export default AdminLayout;
